// pages/VideoPlayer.jsx
import { useEffect, useState, useRef, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Stack,
  Skeleton,
  Card,
  CardContent,
  Avatar,
  Divider,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";

import { getVideo } from "../utils/generalSubjectApi";
import { getYouTubeId } from "../utils/youtube";
import { AuthContext } from "../AuthContext";
import NotEnoughCoinsModal from "../components/modal/notEnoughCoin";

const glass = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.75)",
  border: "1px solid rgba(255,255,255,0.92)",
  boxShadow:
    "0 8px 32px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.95)",
};

/* small related video card */
const RelatedCard = ({
  vid,
  navigate,
  active,
  subjectId,
  examId,
  isAdmin,
}) => {
  const youtubeId = getYouTubeId(vid.video_url);
  const isCloudinary = vid.video_url && !youtubeId;
  const getCloudinaryThumbnail = (url) => {
    if (!url) return null;

    return url
      .replace("/video/upload/", "/video/upload/so_1,q_auto,f_auto/")
      .replace(/\.\w+$/, ".jpg");
  };

  const thumbnail = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
    : isCloudinary
    ? getCloudinaryThumbnail(vid.video_url)
    : null;


  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={() => {
        // 1. Determine the base path
        const basePath = isAdmin ? "admin-videos" : "videos";

        // 2. Navigate to the dynamic URL
        navigate(`/${basePath}/${vid.id}`, {
          state: {
            subjectId,
            examId,
            isAdmin,
          },
        });
      }}
      style={{ cursor: "pointer" }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          p: 1.2,
          borderRadius: "16px",
          background: active
            ? "rgba(99,102,241,0.08)"
            : hovered
            ? "rgba(0,0,0,0.03)"
            : "transparent",
          border: active
            ? "1px solid rgba(99,102,241,0.2)"
            : "1px solid transparent",
          transition: "all 0.2s",
        }}
      >
        {/* thumb */}
        <Box
          sx={{
            width: 112,
            height: 64,
            borderRadius: "12px",
            flexShrink: 0,
            overflow: "hidden",
            position: "relative",
            background: "linear-gradient(135deg,#1e1b4b,#312e81)",
          }}
        >
          {thumbnail ? (
            <Box
              component="img"
              src={thumbnail}
              alt={vid.title}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform 0.3s",
                transform: hovered ? "scale(1.08)" : "scale(1)",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoLibraryRoundedIcon
                sx={{ color: "rgba(255,255,255,0.2)", fontSize: 24 }}
              />
            </Box>
          )}
          {active && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(99,102,241,0.5)",
              }}
            >
              <PlayArrowRoundedIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
          )}
        </Box>

        {/* info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: "'Clash Display', sans-serif",
              fontWeight: 700,
              fontSize: "0.82rem",
              color: active ? "#6366f1" : "#0f172a",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {vid.title}
          </Typography>
              {vid.year && (
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    color: "#64748b",
                    fontWeight: 600,
                    mt: 0.3,
                  }}
                >
                  Year: {vid.year}
                </Typography>
              )}

              {vid.watched && (
               <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: vid.watched ? "#16a34a" : "#ef4444",
                  mt: 0.2,
                }}
              >
                {vid.watched ? "Watched" : "New"}
              </Typography>
              )}
        </Box>
      </Stack>
    </motion.div>
  );
};

export default function VideoPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [totalVideoCost, setTotalVideoCost] = useState(null);
  const [related, setRelated] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const location = useLocation();
  const { subjectId, examId, isAdmin } = location.state || {};
  const { user } = useContext(AuthContext);

  const playerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getVideo(id, examId);

        console.log("API RESPONSE:", res);

        // ✅ NEW STRUCTURE
        setVideo(res.video);
        setTotalVideoCost(res.total_unwatched_coin_cost);
        setRelated(res.related_videos || []);
      } catch (err) {
        console.error(err);
      }
    };

    load();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id, examId]);

  const youtubeId = video ? getYouTubeId(video.video_url) : null;
  const isCloudinaryVideo = video?.video_url && !youtubeId;

  const safeVideoUrl = video?.video_url
    ?.replace(".mov", ".mp4")
    ?.replace("/upload/", "/upload/f_mp4/");

  const getCloudinaryPoster = (url) => {
    if (!url) return "";
    return url
      .replace("/video/upload/", "/video/upload/so_1,q_auto,f_auto/")
      .replace(/\.\w+$/, ".jpg");
  };

  useEffect(() => {
    if (totalVideoCost !== null) {
      setOpenModal(true);
    }
  }, [totalVideoCost]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg,#f8faff 0%,#f1f5ff 40%,#faf8ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ambient orbs */}
      <Box
        sx={{
          position: "fixed",
          top: -200,
          left: -140,
          width: 560,
          height: 560,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(99,102,241,0.1) 0%,transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: -140,
          right: -100,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(16,185,129,0.08) 0%,transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 3, md: 5 },
          pb: 10,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* back */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 4,
              px: 2.5,
              py: 1.2,
              borderRadius: "14px",
              cursor: "pointer",
              ...glass,
              fontWeight: 600,
              color: "#475569",
              fontSize: "0.88rem",
              "&:hover": { color: "#6366f1" },
              transition: "color 0.2s",
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} /> Back
          </Box>
        </motion.div>

        {/* main layout */}
        <Box
          sx={{
            display: "flex",
            gap: 4,
            alignItems: "flex-start",
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {/* ── LEFT: player + info ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* player */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  overflow: "hidden",
                  aspectRatio: "16/9",
                  background: "linear-gradient(135deg,#0f0f23,#1e1b4b)",
                  boxShadow:
                    "0 32px 80px rgba(99,102,241,0.2), 0 8px 24px rgba(0,0,0,0.15)",
                }}
              >
                {!video ? (
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "100%",
                      height: "100%",
                      background: "rgba(99,102,241,0.08)",
                    }}
                  />
                ) : youtubeId ? (
                  <Box
                    ref={playerRef}
                    component="iframe"
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    sx={{
                      display: "block",
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : isCloudinaryVideo ? (
                  <Box
                    component="video"
                    src={safeVideoUrl}
                    poster={getCloudinaryPoster(video.video_url)}
                    controls
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      backgroundColor: "#000",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <VideoLibraryRoundedIcon
                      sx={{ color: "rgba(255,255,255,0.2)", fontSize: 64 }}
                    />
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: "0.9rem",
                      }}
                    >
                      Invalid video URL
                    </Typography>
                  </Box>
                )}
              </Box>
            </motion.div>

            {/* title + actions */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Box sx={{ mt: 3 }}>
                {!video ? (
                  <>
                    <Skeleton
                      variant="text"
                      width="85%"
                      height={36}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={22} />
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontFamily: "'Clash Display', sans-serif",
                        fontWeight: 700,
                        fontSize: { xs: "1.3rem", md: "1.65rem" },
                        color: "#0f172a",
                        lineHeight: 1.25,
                        letterSpacing: "-0.03em",
                        mb: 1.5,
                      }}
                    >
                      {video.title}
                    </Typography>

                    {/* meta row */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                      gap={2}
                      flexWrap="wrap"
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            background:
                              "linear-gradient(135deg,#6366f1,#8b5cf6)",
                            fontSize: "0.75rem",
                            fontWeight: 800,
                          }}
                        >
                          {video.subject_name
                            ? video.subject_name[0].toUpperCase()
                            : "V"}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.88rem",
                              color: "#0f172a",
                            }}
                          >
                            {video.subject_name || "Course"}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.72rem", color: "#94a3b8" }}
                          >
                            {video.category || "No category"}
                          </Typography>

                          {video.year && (
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: "#64748b",
                                fontWeight: 600,
                                mt: 0.3,
                              }}
                            >
                              Year: {video.year}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Stack>
                  </>
                )}
              </Box>
            </motion.div>

            {/* description card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card
                sx={{
                  ...glass,
                  borderRadius: "20px",
                  mt: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: 3,
                    background:
                      "linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)",
                  }}
                />
                <CardContent sx={{ p: "20px 24px !important" }}>
                  <Typography
                    sx={{
                      fontFamily: "'Clash Display', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      color: "#94a3b8",
                      letterSpacing: "0.08em",
                      mb: 1.5,
                    }}
                  >
                    ABOUT THIS LESSON
                  </Typography>
                  <Typography
                    sx={{
                      color: "#475569",
                      fontSize: "0.9rem",
                      lineHeight: 1.7,
                    }}
                  >
                    {video?.description ||
                      "Watch this video lesson and expand your knowledge. Take notes, pause and rewind as needed, and explore the related videos in this subject."}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Box>

          {/* ── RIGHT: related videos ── */}
          <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card
                sx={{
                  ...glass,
                  borderRadius: "24px",
                  overflow: "hidden",
                  position: "sticky",
                  top: 24,
                }}
              >
                <Box
                  sx={{
                    height: 3,
                    background: "linear-gradient(90deg,#8b5cf6,#6366f1)",
                  }}
                />
                <CardContent sx={{ p: "20px !important" }}>
                  <Typography
                    sx={{
                      fontFamily: "'Clash Display', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.96rem",
                      color: "#0f172a",
                      mb: 0.5,
                    }}
                  >
                    Up Next
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "#94a3b8",
                      fontWeight: 600,
                      mb: 2.5,
                    }}
                  >
                    {video?.category
                      ? `${related.length} in ${video.category}`
                      : `${related.length} videos`}
                  </Typography>

                  <Divider sx={{ mb: 2, borderColor: "rgba(0,0,0,0.06)" }} />

                  <Stack spacing={0.5}>
                    {related.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <VideoLibraryRoundedIcon
                          sx={{ fontSize: 40, color: "#d1d5db", mb: 1 }}
                        />
                        <Typography
                          sx={{ color: "#94a3b8", fontSize: "0.82rem" }}
                        >
                          No related videos
                        </Typography>
                      </Box>
                    ) : (
                      related.map((vid) => (
                        <RelatedCard
                          key={vid.id}
                          vid={vid}
                          navigate={navigate}
                          active={vid.id === parseInt(id)}
                          subjectId={subjectId}
                          examId={examId}
                          isAdmin={isAdmin}
                        />
                      ))
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        </Box>
        <NotEnoughCoinsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          totalVideoCost={totalVideoCost}
          user={user}
        />
      </Box>
    </Box>
  );
}
